import { Notify, toast } from "@/components/ui/notify";
import type { ParameterDetail } from "@/features/line-editor/hooks";
import { getFriendlyErrorMessage, isApiClientError } from "@/lib/api-client";
import type { CreateParameterRequest } from "@opus-dt/shared-types";
import type { useMachineSidebarBridge } from "../hooks/useMachineSidebarBridge";
import { buildCounterUpdatePatch, buildPayload } from "./build-payload";
import type { ParamForm } from "./types";

interface Args {
  bridge: ReturnType<typeof useMachineSidebarBridge>;
  form: ParamForm;
  editingId: string | null;
  setIsPending: (v: boolean) => void;
  onDone: () => void;
}

/**
 * Spec 081 — salvar e remover parâmetro, extraídos do orquestrador da aba pelo teto de 200 linhas
 * efetivas. Nenhuma regra nova mora aqui: é o mesmo fluxo, incluindo a troca do papel visual da
 * cena 3D (Spec 078 FR-016) e a confirmação por nome da remoção.
 */
export function useParameterActions({ bridge, form, editingId, setIsPending, onDone }: Args) {
  async function save(payload: CreateParameterRequest) {
    if (editingId) {
      // Spec 081 — no update os campos do contador viajam como `null` quando limpos, não como
      // ausência: `undefined` significa "não mexa", e reusar o payload de criação tornava
      // impossível desvincular o produto ou apagar a equivalência própria.
      await bridge.updateParameter(editingId, { ...payload, ...buildCounterUpdatePatch(form) });
      toast.success("Parâmetro atualizado.");
    } else {
      await bridge.addParameter(payload);
      toast.success("Parâmetro criado.");
    }
  }

  function visualRoleTaken(err: unknown): { parameterId: string } | null {
    if (!isApiClientError(err) || err.error.code !== "VISUAL_ROLE_TAKEN") return null;
    const id = (err.error as { details?: { parameterId?: unknown } }).details?.parameterId;
    return typeof id === "string" ? { parameterId: id } : null;
  }

  async function handleSubmit() {
    if (!form.category) {
      toast.error("Selecione uma categoria.");
      return;
    }
    if (!form.name.trim()) {
      toast.error("Informe o nome do parâmetro.");
      return;
    }
    if (form.category === "STATUS" && form.statusMapping.length === 0) {
      toast.error("Configure pelo menos 1 mapeamento valor → status.");
      return;
    }

    const description = form.description.trim() || undefined;
    const name = form.name.trim();
    const payload: CreateParameterRequest = buildPayload(form, name, description);

    setIsPending(true);
    try {
      await save(payload);
      onDone();
    } catch (err) {
      // Spec 078 FR-016 — outro parâmetro já tem o papel: oferece TROCAR (retira do antigo, atribui ao novo).
      const taken = visualRoleTaken(err);
      if (taken) {
        const swap = await Notify.confirm({
          variant: "warning",
          title: "Trocar o papel na cena 3D?",
          description: getFriendlyErrorMessage(err),
          confirmLabel: "Trocar",
          cancelLabel: "Manter como está",
        });
        if (swap) {
          try {
            await bridge.updateParameter(taken.parameterId, { visualRole: "NONE" });
            await save(payload);
            onDone();
            return;
          } catch (err2) {
            toast.error("Falha ao trocar o papel.", { description: getFriendlyErrorMessage(err2) });
            return;
          }
        }
        return;
      }
      const msg = getFriendlyErrorMessage(err);
      toast.error("Falha ao salvar parâmetro.", { description: msg });
    } finally {
      setIsPending(false);
    }
  }

  async function handleDelete(p: ParameterDetail) {
    const ok = await Notify.confirm({
      variant: "danger",
      title: `Remover o parâmetro "${p.name}"?`,
      description:
        "Esta ação não pode ser desfeita. Para confirmar, digite o nome do parâmetro abaixo.",
      confirmLabel: "Remover",
      typeToConfirm: p.name,
    });
    if (!ok) return;
    try {
      await bridge.removeParameter(p.id);
    } catch (err) {
      // Erro de API já foi mostrado pelo onError de useDeleteParameter (skipErrorToast:true
      // só suprime o toast global); aqui só sobra o throw síncrono do caminho draft.
      if (isApiClientError(err)) return;
      Notify.toast({
        variant: "danger",
        title: "Falha ao excluir parâmetro",
        description: getFriendlyErrorMessage(err),
      });
    }
  }

  return { handleSubmit, handleDelete };
}
